/* Client register — the CRM "client book" landing (#/clients), Package 1.
 *
 * A frontend port of the owner's approved design
 * (design_handoff_client_intelligence_hub/crm-hub/index.html lines 52–108):
 * a greeting head, a portfolio-workload summary strip, a filter/view toolbar,
 * and one `.account-spine` row per client. It REPLACES the old My Clients
 * card grid (views/myClients.ts) as the login landing.
 *
 * Honesty rules (handoff §6.7 + CLAUDE.md provenance-must-render): EVERY cell
 * is derived from the live `GET /clients` rollup (tools/ws_profiles.py::
 * portfolio) — this module computes no numbers the payload doesn't carry.
 * Absent data renders "not connected" / "not run", never a fabricated zero:
 *   - Meetings: no calendar source exists → "—" + "Google Calendar not
 *     connected" (plain text, no dead Connect button).
 *   - Upcoming work: real counts only (open · overdue · waiting) — the rollup
 *     carries no task titles, so none are shown.
 *   - Research freshness: presence/search/AI chips from planes.*.lastRun — a
 *     real date or an honest "not run".
 * "Verified this week" is deliberately omitted: it is NOT derivable from this
 * payload, so no summary cell fabricates it.
 *
 * Lit conventions follow views/wiki.ts's convention header exactly (import from
 * 'lit'; render(html`…`, el); `@click` handlers; `nothing` for absent branches).
 *
 * ONE fetch, shared: initRegister() owns the single GET /clients and notifies
 * onClientsLoaded() subscribers (main.ts populates the rail badge + pinned
 * clients from the same rows — no second fetch).
 */
import { html, render, type TemplateResult } from 'lit';
import { ApiError, request } from '../api/client.ts';
import { AUTH } from '../lib/auth.ts';
import { brandVisual, type BrandVisual } from '../lib/identity.ts';

/** One client from the GET /clients rollup (tools/ws_profiles.py::portfolio).
 * Every field the register reads; extra rollup fields are ignored. */
export interface RegisterClient {
  slug: string;
  brand: string;
  descriptor?: string | null;
  domain?: string | null;
  logoUrl?: string | null;
  lastRun?: string | null;
  lastRunFile?: string | null;
  // Workload counters — OPTIONAL by contract (review #11): a rollup that omits
  // a field must render "not connected", never a fabricated healthy 0. num()
  // distinguishes an absent field (null) from a real measured 0.
  open?: number;
  high?: number;
  regressed?: number;
  pendingVerification?: number;
  attested?: number;
  planes?: {
    presence?: { lastRun?: string | null } | null;
    search?: { lastRun?: string | null } | null;
    ai?: { lastRun?: string | null } | null;
  };
  waiting?: number;
  stalled?: number;
  overdue?: number;
  owners?: string[];
  oldestOpenDays?: number | null;
  attention?: string;
  attentionScore?: number;
}

/** A field that is a real finite number, or null when the rollup omitted it —
 * so a missing counter never masquerades as a measured 0 (review #11). */
function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

type FilterChip = 'all' | 'open' | 'recent';
type RegisterView = 'table' | 'cards' | 'compact';

const VIEW_KEY = 'crm-register-view';
/** "Recently updated" window — a client whose latest run is within this many
 * days. A real client-side filter over the rollup's lastRun, not a sort. */
const RECENT_DAYS = 30;

let rows: RegisterClient[] = [];
let filterText = '';
let activeChip: FilterChip = 'all';
let view: RegisterView = loadView();
let loaded = false;

type RowsListener = (rows: RegisterClient[]) => void;
const listeners: RowsListener[] = [];

/** Subscribe to the shared roster (rail badge + pinned clients in main.ts).
 * Fires immediately with the current rows if they're already loaded. */
export function onClientsLoaded(cb: RowsListener): void {
  listeners.push(cb);
  if (loaded) cb(rows);
}

function loadView(): RegisterView {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    if (v === 'table' || v === 'cards' || v === 'compact') return v;
  } catch { /* storage unavailable */ }
  return 'table';
}

function saveView(v: RegisterView): void {
  try { localStorage.setItem(VIEW_KEY, v); } catch { /* storage unavailable */ }
}

function ageDays(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : Math.floor((Date.now() - t) / 864e5);
}

/** ISO → "Jul 7" (the design's compact run-date grammar). */
function shortDate(iso?: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function firstName(name: string | null | undefined): string {
  const w = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  return w[0] || 'there';
}

/** Time-of-day greeting part + human copy, from local time. */
function greetingParts(): { part: string; greeting: string } {
  const h = new Date().getHours();
  const part = h < 5 ? 'night' : h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
  const greeting =
    part === 'night' ? 'Working late'
      : part === 'morning' ? 'Good morning'
        : part === 'afternoon' ? 'Good afternoon'
          : 'Good evening';
  return { part, greeting };
}

/** The `.brand-logo` node for a client: logoUrl → favicon → neutral non-letter
 * mark (locked contract §5 — never initials). Exported so the pinned-clients
 * rail (main.ts) renders identical marks from the shared rows. */
export function brandLogo(v: BrandVisual, extraClass = ''): TemplateResult {
  const cls = `brand-logo ${extraClass}`.trim();
  if (v.kind === 'neutral') {
    // Neutral generic mark, tokens-only (no raw hex, no letters).
    return html`<span class=${cls}><svg viewBox="0 0 24 24" aria-hidden="true"
      style="width:100%;height:100%;display:block">
      <rect x="4.5" y="4.5" width="15" height="15" rx="3.5" fill="none"
        stroke="var(--crm-border-strong)" stroke-width="1.4"></rect>
      <circle cx="12" cy="12" r="3.1" fill="var(--crm-tertiary)"></circle>
    </svg></span>`;
  }
  return html`<span class=${cls}><img src=${v.src} alt="" loading="lazy"></span>`;
}

function matchesFilters(r: RegisterClient): boolean {
  if (activeChip === 'open' && (num(r.open) ?? 0) <= 0) return false;
  if (activeChip === 'recent') {
    const age = ageDays(r.lastRun);
    if (age === null || age > RECENT_DAYS) return false;
  }
  if (filterText) {
    const hay = `${r.brand} ${r.descriptor ?? ''} ${r.domain ?? ''}`.toLowerCase();
    if (!hay.includes(filterText)) return false;
  }
  return true;
}

/* ── head + summary ─────────────────────────────────────────────────────── */

function headView(): TemplateResult {
  const { part, greeting } = greetingParts();
  const name = firstName(AUTH.user?.name);
  const date = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
  const n = rows.length;
  const clientCount = `${n} ${n === 1 ? 'client' : 'clients'}`;
  return html`<div class="portfolio-head">
    <div>
      <span class="eyebrow" id="greeting-date-row">
        <span class="daymark" data-part=${part} aria-hidden="true">${daymarkIcon(part)}</span>
        <span>${date} · account management · ${clientCount}</span>
      </span>
      <h1><span>${greeting}, ${name}</span></h1>
      <p>Here's your client book — open one for its program, research, tasks and evidence.</p>
    </div>
  </div>`;
}

function daymarkIcon(part: string) {
  return part === 'evening' || part === 'night'
    ? html`<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M15.5 12.2A6.5 6.5 0 0 1 7.8 4.5 6.5 6.5 0 1 0 15.5 12.2Z" fill="currentColor"></path></svg>`
    : html`<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="4" fill="currentColor"></circle><g stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="10" y1="1.4" x2="10" y2="3.2"></line><line x1="10" y1="16.8" x2="10" y2="18.6"></line><line x1="1.4" y1="10" x2="3.2" y2="10"></line><line x1="16.8" y1="10" x2="18.6" y2="10"></line><line x1="3.9" y1="3.9" x2="5.2" y2="5.2"></line><line x1="14.8" y1="14.8" x2="16.1" y2="16.1"></line><line x1="16.1" y1="3.9" x2="14.8" y2="5.2"></line><line x1="5.2" y1="14.8" x2="3.9" y2="16.1"></line></g></svg>`;
}

/** Portfolio-workload strip — ONLY cells derivable from the live rollup. Stat
 * tiles (not interactive filters): the real filters are the toolbar chips.
 * "Verified this week" is intentionally absent (not derivable — see file doc). */
/** Aggregate ONE counter across the roster, over PRESENT fields only (FIX-audit
 * F6): a client that omits the field contributes NOTHING to the sum AND is
 * counted as `missing`, so the strip never presents a complete-looking total
 * over incomplete data (the old code coerced absent → 0 into the sum, silently
 * dropping those clients while the label implied the total was whole). */
function aggCounter(pick: (r: RegisterClient) => number | null): {
  sum: number; withValue: number; reporting: number; missing: number;
} {
  let sum = 0, withValue = 0, reporting = 0, missing = 0;
  for (const r of rows) {
    const v = pick(r);
    if (v === null) { missing++; continue; }
    reporting++; sum += v;
    if (v > 0) withValue++;
  }
  return { sum, withValue, reporting, missing };
}

/** " · N not connected" tail when some clients omit this counter, else ''. */
function missingTail(missing: number): string {
  return missing ? ` · ${missing} not connected` : '';
}

function summaryView(): TemplateResult {
  const total = rows.length;
  const attn = aggCounter((r) => num(r.attentionScore));
  const attnRows = rows.filter((r) => (num(r.attentionScore) ?? 0) > 0);
  const attnNames = [...attnRows]
    .sort((a, b) => (num(b.attentionScore) ?? 0) - (num(a.attentionScore) ?? 0))
    .slice(0, 3)
    .map((r) => r.brand)
    .join(' · ');
  const open = aggCounter((r) => num(r.open));
  const waiting = aggCounter((r) => num(r.waiting));
  const awaiting = aggCounter((r) => num(r.pendingVerification));

  // A counter no client reports renders "—", never a fabricated 0.
  const cell = (label: string, value: number | string, sub: string): TemplateResult => html`
    <div class="summary-cell">
      <span>${label}</span><strong>${value}</strong><small>${sub}</small>
    </div>`;
  const val = (a: { sum: number; reporting: number }): number | string => (a.reporting ? a.sum : '—');

  return html`<div class="portfolio-summary" aria-label="Portfolio workload">
    ${cell('All clients', total, 'your book')}
    ${cell('Open tasks', val(open), open.reporting
      ? `${open.withValue ? `across ${open.withValue} client${open.withValue === 1 ? '' : 's'}` : 'nothing open'}${missingTail(open.missing)}`
      : 'task ledger not connected')}
    ${cell('Needs attention', attn.reporting ? attnRows.length : '—', attn.reporting
      ? `${attnRows.length ? attnNames : 'all clear'}${missingTail(attn.missing)}`
      : 'not scored')}
    ${cell('Waiting on others', val(waiting), waiting.reporting
      ? `${waiting.withValue ? `${waiting.withValue} client${waiting.withValue === 1 ? '' : 's'}` : 'nothing waiting'}${missingTail(waiting.missing)}`
      : 'not connected')}
    ${cell('Awaiting verification', val(awaiting), awaiting.reporting
      ? `${awaiting.sum ? `evidence-gated · ${awaiting.withValue} client${awaiting.withValue === 1 ? '' : 's'}` : 'none pending'}${missingTail(awaiting.missing)}`
      : 'not connected')}
  </div>`;
}

/* ── toolbar ────────────────────────────────────────────────────────────── */

function setChip(chip: FilterChip): void {
  activeChip = chip;
  paint();
}

function setView(v: RegisterView): void {
  view = v;
  saveView(v);
  paint();
}

function toolbarView(): TemplateResult {
  const chip = (id: FilterChip, label: string): TemplateResult => html`
    <button class=${activeChip === id ? 'active' : ''} @click=${() => setChip(id)}>${label}</button>`;
  const vbtn = (id: RegisterView, label: string, glyph: TemplateResult): TemplateResult => html`
    <button data-view=${id} class=${view === id ? 'active' : ''} title=${label} aria-label=${`${label} view`}
      @click=${() => setView(id)}>${glyph}</button>`;
  return html`<div class="client-register-tools">
    <div class="register-search">
      <span>⌕</span>
      <input id="registerFilter" type="search" aria-label="Filter clients" placeholder="Filter clients"
        .value=${filterText}
        @input=${(ev: Event) => { filterText = (ev.target as HTMLInputElement).value.trim().toLowerCase(); paint(); }}>
    </div>
    <div class="filter-group" aria-label="Client filters">
      ${chip('all', 'All clients')}
      ${chip('open', 'Has open tasks')}
      ${chip('recent', 'Recently updated')}
    </div>
    <div class="view-toggle view-toggle-icons" role="group" aria-label="Register view">
      ${vbtn('table', 'Table', html`<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="2.5" width="13" height="2.4" rx="0.6"></rect><rect x="1.5" y="6.8" width="13" height="2.4" rx="0.6"></rect><rect x="1.5" y="11.1" width="13" height="2.4" rx="0.6"></rect></svg>`)}
      ${vbtn('cards', 'Cards', html`<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="1.5" width="5.8" height="5.8" rx="1"></rect><rect x="8.7" y="1.5" width="5.8" height="5.8" rx="1"></rect><rect x="1.5" y="8.7" width="5.8" height="5.8" rx="1"></rect><rect x="8.7" y="8.7" width="5.8" height="5.8" rx="1"></rect></svg>`)}
      ${vbtn('compact', 'Compact', html`<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="2" width="13" height="1.6" rx="0.5"></rect><rect x="1.5" y="5.5" width="13" height="1.6" rx="0.5"></rect><rect x="1.5" y="9" width="13" height="1.6" rx="0.5"></rect><rect x="1.5" y="12.5" width="13" height="1.6" rx="0.5"></rect></svg>`)}
    </div>
  </div>`;
}

/* ── one client row ─────────────────────────────────────────────────────── */

function freshChip(label: string, iso?: string | null): TemplateResult {
  const d = shortDate(iso);
  return d
    ? html`<i class="fresh-chip ok">${label} · ${d}</i>`
    : html`<i class="fresh-chip none">${label} · not run</i>`;
}

function workCell(r: RegisterClient): TemplateResult {
  const open = num(r.open);
  if (open === null) {
    // No task-ledger field on the rollup — honest absence, not "0 open" (review #11).
    return html`<div class="work-snapshot empty-value" role="cell">
      <strong>—</strong><span>Task ledger not connected</span></div>`;
  }
  // FIX-audit F6: distinguish an ABSENT gate counter from a measured 0 — the
  // secondary counters are optional too, so "no gates" is only honest when they
  // were actually reported. If open is present but the gates are all absent,
  // say "gates not reported", never a fabricated clean "no gates".
  const overdueN = num(r.overdue), waitingN = num(r.waiting), highN = num(r.high);
  const gatesReported = overdueN !== null || waitingN !== null || highN !== null;
  const overdue = overdueN ?? 0, waiting = waitingN ?? 0, high = highN ?? 0;
  if (open <= 0 && overdue <= 0 && waiting <= 0) {
    return html`<div class="work-snapshot empty-value" role="cell">
      <strong>—</strong><span>No open work</span></div>`;
  }
  const extras: string[] = [];
  if (overdue > 0) extras.push(`${overdue} overdue`);
  if (waiting > 0) extras.push(`${waiting} waiting`);
  if (high > 0) extras.push(`${high} high`);
  return html`<div class="work-snapshot" role="cell">
    <strong>${open}</strong><span>open task${open === 1 ? '' : 's'}</span>
    <small class=${overdue > 0 ? 'overdue-note' : ''}>${extras.length ? extras.join(' · ') : (gatesReported ? 'no gates' : 'gates not reported')}</small>
  </div>`;
}

/** Honest per-row status, derived from the REAL attentionScore — not the
 * fabricated "Active" pill the mockup carried (review #10): >0 needs attention,
 * a measured 0/negative is on track, an ABSENT score shows no pill at all. */
function statusPill(r: RegisterClient): TemplateResult {
  const score = num(r.attentionScore);
  if (score === null) return html``;
  return score > 0
    ? html`<span class="status-label attention">Needs attention</span>`
    : html`<span class="status-label calm">On track</span>`;
}

function rowView(r: RegisterClient): TemplateResult {
  const hasResearch = !!(r.lastRun || r.lastRunFile);
  const logoV = brandVisual(r);
  const go = (ev: Event): void => {
    if ((ev.target as HTMLElement).closest('button, a')) return;
    location.hash = `#/client/${r.slug}`;
  };
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); location.hash = `#/client/${r.slug}`; }
  };
  const p = r.planes || {};
  const runDate = shortDate(r.lastRun);
  return html`<article
    class=${`account-spine ${hasResearch ? '' : 'secondary-record'}`.trim()}
    tabindex="0" role="row" @click=${go} @keydown=${onKey}>
    <div class="account-name" role="cell">
      ${brandLogo(logoV, 'large')}
      <div>
        <h2>${r.brand}</h2>
        <p>${r.descriptor || 'Client record'}</p>
        ${statusPill(r)}
      </div>
    </div>
    <div class="program-snapshot empty-value" role="cell">
      <strong>—</strong><span>Google Calendar not connected</span>
    </div>
    ${workCell(r)}
    <div class="research-snapshot" role="cell">
      <span class="fresh-row">
        ${freshChip('Presence', p.presence?.lastRun)}
        ${freshChip('Search', p.search?.lastRun)}
        ${freshChip('AI', p.ai?.lastRun)}
      </span>
      <small>${runDate ? `Last run ${runDate}` : 'No research run yet'}</small>
    </div>
    <div class="account-entry" role="cell">
      <div class="entry-quick">
        <button title=${`${num(r.open) ?? 0} open task${(num(r.open) ?? 0) === 1 ? '' : 's'}`}
          @click=${(ev: Event) => { ev.stopPropagation(); location.hash = `#/client/${r.slug}/board`; }}>
          Tasks<b>${num(r.open) ?? 0}</b>
        </button>
        <button @click=${(ev: Event) => { ev.stopPropagation(); location.hash = `#/client/${r.slug}`; }}>
          Research
        </button>
      </div>
    </div>
  </article>`;
}

function registerView(): TemplateResult {
  const visible = rows.filter(matchesFilters);
  return html`<div class=${`client-register view-${view}`} role="table" aria-label="Client book">
    <div class="register-header" role="row">
      <span role="columnheader">Client</span>
      <span role="columnheader">Meetings</span>
      <span role="columnheader">Upcoming work</span>
      <span role="columnheader">Research freshness</span>
      <span role="columnheader">Jump in</span>
    </div>
    ${visible.length
      ? visible.map(rowView)
      : html`<div class="register-empty" style="padding:var(--sp-4);color:var(--crm-tertiary);font-size:.72rem">No clients match this filter.</div>`}
  </div>`;
}

function view_(): TemplateResult {
  if (!loaded) {
    return html`<div class="register-empty" style="padding:var(--sp-5);color:var(--crm-tertiary);font-size:.78rem">Loading your client book…</div>`;
  }
  if (!rows.length) {
    return html`<div class="register-empty" style="padding:var(--sp-5);color:var(--crm-tertiary);font-size:.78rem">No clients indexed yet —
      run research on a client and it appears here.</div>`;
  }
  return html`${headView()}${summaryView()}${toolbarView()}${registerView()}`;
}

function paint(): void {
  const host = document.getElementById('clientsView');
  if (host) render(view_(), host);
}

/** Fetch the roster and paint the register. Called when the clients area is
 * shown (boot when it's the landing, or on nav into it — main.ts), matching
 * the old My Clients freshness-on-entry behavior. A 401 is handled by the api
 * client (it re-shows the sign-in gate). */
export async function initRegister(): Promise<void> {
  const host = document.getElementById('clientsView');
  if (!host) return;
  if (!loaded) paint(); // loading state on first entry
  try {
    rows = (await request<{ clients?: RegisterClient[] }>('/clients')).clients || [];
    loaded = true;
    paint();
    for (const cb of listeners) cb(rows);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return; // gate is showing
    loaded = true;
    render(
      html`<div class="register-empty" style="padding:var(--sp-5);color:var(--crm-tertiary);font-size:.78rem">Workspace service unreachable —
        start the service and reload.</div>`,
      host,
    );
  }
}

/** The topbar search trigger's target: land on the register and focus its
 * filter input. No global search backend exists — this is the honest behavior. */
export function focusRegisterFilter(): void {
  if (location.hash !== '#/clients') location.hash = '#/clients';
  requestAnimationFrame(() => {
    (document.getElementById('registerFilter') as HTMLInputElement | null)?.focus();
  });
}
